import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { saveCheck } from "../lib/api";
import { usePricing } from "../lib/usePricing";

// Verified-verdict card capture — "email me if this verdict changes".
export default function SaveCheckBox({ result }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pricing = usePricing();

  const pairs = useMemo(() => {
    const findings = Array.isArray(result?.verified_findings) ? result.verified_findings : [];
    const seen = new Set();
    const out = [];
    findings.forEach((f) => {
      const key = `${f.medication_id}:${f.ingredient_id}`;
      if (f.medication_id && f.ingredient_id && !seen.has(key)) {
        seen.add(key);
        out.push({
          medication_id: f.medication_id,
          ingredient_id: f.ingredient_id,
          medication_name: f.medication_name,
          ingredient_name: f.ingredient_name,
        });
      }
    });
    return out;
  }, [result]);

  const medNames = (result?.medications || []).map((m) => m.name).filter(Boolean);

  if (pairs.length === 0) return null;

  const submit = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    try {
      await saveCheck({
        email: e,
        verdict: result?.verdict,
        product_name: result?.product_name,
        medication_names: medNames,
        pairs,
      });
      setDone(true);
    } catch (_err) {
      toast.error("Couldn't save that — check the email and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="save-check-box" className="rounded-2xl border border-surface-line bg-white px-5 py-5">
      {done ? (
        <div data-testid="save-check-done" className="flex items-start gap-3">
          <Check className="w-5 h-5 text-safe-deep shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-[14px] text-safe-deep leading-relaxed">
            Saved. We'll only email you if something changes.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-cream-100 border border-surface-line text-ink-soft shrink-0">
              <Bookmark className="w-4 h-4" />
            </span>
            <h3 className="text-[15px] font-bold text-ink">Save this check</h3>
          </div>
          <p className="text-[13.5px] text-ink-soft leading-relaxed">
            Get an email if this result changes or a new source is added.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="save-check-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="you@email.com"
              className="flex-1 h-11 px-3.5 rounded-xl border border-surface-line bg-cream-50 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-peach-100"
            />
            <button
              type="button"
              data-testid="save-check-submit"
              onClick={submit}
              disabled={busy || !email.trim()}
              className="h-11 px-5 rounded-xl bg-ink text-cream-50 hover:bg-ink-soft text-[14px] font-bold transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
      <Link
        to="/pricing"
        data-testid="save-check-pro-link"
        className="mt-3 inline-flex items-center gap-1 text-[12.5px] text-ink-mute hover:text-ink transition-colors"
      >
        Want your derm PDF and full-routine checks? Pro, {pricing.formatted}/{pricing.interval}
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
